/**
 * TabChartCoordinator - Multi-tab coordinator for chart rendering (refactored from TabChartRenderer)
 *
 * Clean separation of concerns:
 * - DOM structure management and container bounds
 * - Event coordination between specialized renderers
 * - Tab lifecycle and visibility management
 * - No direct rendering - delegates to GrahaChakraRenderer and MuhurtaChakraDivisionsRenderer
 */

import { EventStream } from '../core/EventStream';
import { devLog, devWarn, devError } from '../../../../../utils/devLog';
import { ContainerBounds } from '../engines/ElementCalculationEngine';
import * as d3 from 'd3';
import { chartColorPresetManager } from '../../../../../config/chartColorPresets';
import { getDefaultMuhurtaChakraVisibility } from '../../../../../config/muhurtaDefaults';
import { ReadyChartData } from '../../../../../types/ReadyChartData';

export interface ChartCoordinatorOptions {
    displayMode: 'symbols' | 'short';
    enableAnimations: boolean;
    debugMode: boolean;
    showGrahaChakra: boolean;     // NEW: Control Graha Chakra visibility
    showMuhurtaChakra: boolean;   // Renamed from showMuhurtaSectors
}

/**
 * Tab-aware chart coordinator with event-driven architecture
 */
export class TabChartCoordinator {
    private eventStream: EventStream;
    private containers: Map<string, ContainerBounds> = new Map();
    private isInitialized: boolean = false;
    private chartId: string;
    private eventSubscriptions: Array<{ unsubscribe: () => void }> = [];

    // Current chart data for re-rendering when needed
    private currentChartData: ReadyChartData | null = null;

    // Current options
    private currentOptions: ChartCoordinatorOptions = {
        displayMode: 'short',
        enableAnimations: false,
        debugMode: false,
        showGrahaChakra: true,                          // NEW: Default enabled
        showMuhurtaChakra: getDefaultMuhurtaChakraVisibility()
    };

    // Coordinator status tracking
    private coordinatorStatus = {
        tab: {
            isActive: false,             // Active tab
            isDOMReady: false,           // DOM structure ready
            readyTime: 0                 // Initialization time
        },
        chakras: {
            grahaRequested: false,       // Graha render requested
            muhurtaRequested: false,     // Muhurta render requested
            grahaCompleted: false,       // Graha render completed
            muhurtaCompleted: false      // Muhurta render completed
        }
    };

    // DOM initialization state tracking
    private domInitialized = false;
    private containersReadyEmitted = false;

    constructor(eventStream: EventStream, debugMode: boolean = false, chartId: string) {
        this.eventStream = eventStream;
        this.currentOptions.debugMode = debugMode;
        this.chartId = chartId;

        this.setupEventSubscriptions();

        // Register this coordinator globally for lazy management
        if (!window.tabChartCoordinators) {
            window.tabChartCoordinators = new Map();
        }
        window.tabChartCoordinators.set(chartId, this);
        devLog(`🗂️ TabChartCoordinator[${chartId}]: Registered in global registry`);

        if (debugMode) {
            devLog(`🔧 TabChartCoordinator[${chartId}]: Initialized in debug mode`);
        }
    }

    /**
     * Main coordination method - delegates to specialized renderers
     */
    async coordinateChart(chartData: ReadyChartData, options?: Partial<ChartCoordinatorOptions>): Promise<{
        success: boolean;
        grahaRequested: boolean;
        muhurtaRequested: boolean;
        renderTime: number;
    }> {
        const startTime = performance.now();

        devLog(`🎯 TabChartCoordinator[${this.chartId}]: coordinateChart called with ${chartData.planets?.length} planets`);

        // Store current chart data
        this.currentChartData = chartData;

        // Update options
        if (options) {
            this.currentOptions = { ...this.currentOptions, ...options };
        }

        // Sync chakra visibility from chart settings
        this.syncChakraSettings();

        // Ensure DOM initialization
        if (!this.isInitialized) {
            this.initializeDOMStructure();

            if (!this.isInitialized) {
                devWarn(`⚠️ TabChartCoordinator[${this.chartId}]: Cannot coordinate - initialization failed`);
                return { success: false, grahaRequested: false, muhurtaRequested: false, renderTime: performance.now() - startTime };
            }
        }

        // Validate required data
        if (!chartData.planets || chartData.planets.length === 0) {
            devError(`❌ TabChartCoordinator[${this.chartId}]: No planets data provided`);
            return { success: false, grahaRequested: false, muhurtaRequested: false, renderTime: performance.now() - startTime };
        }

        if (!chartData.lagna) {
            devError(`❌ TabChartCoordinator[${this.chartId}]: No lagna data provided`);
            return { success: false, grahaRequested: false, muhurtaRequested: false, renderTime: performance.now() - startTime };
        }

        let grahaRequested = false;
        let muhurtaRequested = false;

        // Request Graha Chakra rendering if enabled and chart is visible
        if (this.shouldRenderGraha()) {
            grahaRequested = this.requestGrahaRender(chartData);
        }

        // Request Muhurta Chakra rendering if enabled
        if (this.shouldRenderMuhurta()) {
            muhurtaRequested = this.requestMuhurtaRender(chartData);
        }

        const renderTime = performance.now() - startTime;

        // Update coordinator status
        this.coordinatorStatus.tab.isDOMReady = true;
        this.coordinatorStatus.chakras.grahaRequested = grahaRequested;
        this.coordinatorStatus.chakras.muhurtaRequested = muhurtaRequested;

        devLog(`🎯 TabChartCoordinator[${this.chartId}]: Coordination completed - Graha: ${grahaRequested}, Muhurta: ${muhurtaRequested}`);

        return {
            success: true,
            grahaRequested,
            muhurtaRequested,
            renderTime
        };
    }

    /**
     * Determine if Graha Chakra should be rendered
     */
    private shouldRenderGraha(): boolean {
        return this.currentOptions.showGrahaChakra && this.isChartVisible();
    }

    /**
     * Determine if Muhurta Chakra should be rendered
     */
    private shouldRenderMuhurta(): boolean {
        return this.currentOptions.showMuhurtaChakra;
    }

    /**
     * Request Graha Chakra rendering via events
     */
    private requestGrahaRender(chartData: ReadyChartData): boolean {
        devLog(`🌟 TabChartCoordinator[${this.chartId}]: Requesting Graha Chakra render`);

        const targetSelector = `.lines-overlay-${this.chartId}`;

        this.eventStream.emit({
            id: `graha-render-request-${this.chartId}-${Date.now()}`,
            type: 'graha.render.request',
            source: `tab-chart-coordinator-${this.chartId}`,
            chartId: this.chartId,
            timestamp: Date.now(),
            targetSelector,
            data: {
                planets: chartData.planets,
                lagna: chartData.lagna,
                hora: chartData.hora,
                rawApiPlanets: chartData.rawApiPlanets
            },
            options: {
                displayMode: this.currentOptions.displayMode,
                showHora: true,
                enableAnimations: this.currentOptions.enableAnimations,
                debugMode: this.currentOptions.debugMode
            },
            containerBounds: this.containers
        });

        return true;
    }

    /**
     * Request Muhurta Chakra rendering via events
     */
    private requestMuhurtaRender(chartData: ReadyChartData): boolean {
        devLog(`📏 TabChartCoordinator[${this.chartId}]: Requesting Muhurta Chakra render`);

        const targetSelector = `.muhurta-sectors-group-${this.chartId}`;
        const sunriseLongitude = chartData.astronomical.sunrise_surya_longitude;

        if (!sunriseLongitude) {
            devError(`❌ TabChartCoordinator[${this.chartId}]: Missing sunrise longitude for muhurta`);
            return false;
        }

        this.eventStream.emit({
            id: `muhurta-render-request-${this.chartId}-${Date.now()}`,
            type: 'muhurta.render.request',
            source: `tab-chart-coordinator-${this.chartId}`,
            chartId: this.chartId,
            timestamp: Date.now(),
            targetSelector,
            data: {
                sunriseLongitude: sunriseLongitude,
                chartData: chartData
            },
            options: {
                visible: this.currentOptions.showMuhurtaChakra,
                divisions: 30
            }
        });

        return true;
    }

    /**
     * Sync chakra visibility from chart settings
     */
    private syncChakraSettings(): void {
        try {
            const multipleChartsContext = window.multipleChartsContext;
            if (multipleChartsContext && this.chartId) {
                const activeChart = multipleChartsContext.getChart?.(this.chartId);
                if (activeChart?.settings) {
                    // Sync Graha Chakra
                    if (activeChart.settings.showGrahaChakra !== undefined) {
                        this.currentOptions.showGrahaChakra = activeChart.settings.showGrahaChakra;
                    }
                    // Sync Muhurta Chakra
                    if (activeChart.settings.showMuhurtaChakra !== undefined) {
                        this.currentOptions.showMuhurtaChakra = activeChart.settings.showMuhurtaChakra;
                    }
                    devLog(`🔄 TabChartCoordinator[${this.chartId}]: Synced chakra settings - Graha: ${this.currentOptions.showGrahaChakra}, Muhurta: ${this.currentOptions.showMuhurtaChakra}`);
                }
            }
        } catch (error) {
            devWarn(`⚠️ TabChartCoordinator[${this.chartId}]: Failed to sync chakra settings:`, error);
        }
    }

    /**
     * Switch display mode for Graha Chakra
     */
    async switchDisplayMode(mode: 'symbols' | 'short'): Promise<void> {
        if (this.currentOptions.displayMode === mode) return;

        this.currentOptions.displayMode = mode;

        // Emit display mode change for Graha Chakra
        this.eventStream.emit({
            id: `graha-displaymode-change-${this.chartId}-${Date.now()}`,
            type: 'graha.displayMode.change',
            source: `tab-chart-coordinator-${this.chartId}`,
            chartId: this.chartId,
            timestamp: Date.now(),
            displayMode: mode
        });

        devLog(`🔄 TabChartCoordinator[${this.chartId}]: Display mode changed to ${mode}`);
    }

    /**
     * Toggle Graha Chakra visibility
     */
    toggleGrahaChakra(visible: boolean): void {
        this.currentOptions.showGrahaChakra = visible;

        this.eventStream.emit({
            id: `graha-toggle-${this.chartId}-${Date.now()}`,
            type: 'graha.toggle',
            source: `tab-chart-coordinator-${this.chartId}`,
            chartId: this.chartId,
            timestamp: Date.now(),
            visible
        });

        devLog(`👁️ TabChartCoordinator[${this.chartId}]: Graha Chakra toggled to ${visible}`);
    }

    /**
     * Toggle Muhurta Chakra visibility
     */
    toggleMuhurtaChakra(visible: boolean): void {
        this.currentOptions.showMuhurtaChakra = visible;

        this.eventStream.emit({
            id: `muhurta-toggle-${this.chartId}-${Date.now()}`,
            type: 'muhurta.sectors.toggle',
            source: `tab-chart-coordinator-${this.chartId}`,
            chartId: this.chartId,
            timestamp: Date.now(),
            showMuhurtaSectors: visible,
            data: { showMuhurtaSectors: visible }
        });

        devLog(`📏 TabChartCoordinator[${this.chartId}]: Muhurta Chakra toggled to ${visible}`);
    }

    /**
     * Update container bounds
     */
    updateContainers(containers: Map<string, ContainerBounds>): void {
        this.containers = new Map(containers);
        this.isInitialized = this.containers.size > 0;

        if (this.currentOptions.debugMode) {
            devLog(`📦 TabChartCoordinator[${this.chartId}]: Updated containers (${this.containers.size} containers)`);
        }
    }

    /**
     * Initialize DOM structure - using chart ID indexed selectors
     */
    private initializeDOMStructure(): void {
        const viewportContent = d3.select(`.viewport-content-${this.chartId}`);

        if (viewportContent.empty()) {
            if (this.currentOptions.debugMode) {
                devWarn(`⚠️ TabChartCoordinator[${this.chartId}]: Viewport .viewport-content-${this.chartId} not found, retrying in 100ms...`);
            }

            this.scheduleEventBasedInitialization();
            return;
        }

        devLog(`✅ TabChartCoordinator[${this.chartId}]: Found viewport, proceeding with initialization`);

        // Create standard containers with chart ID namespace
        this.createStandardContainersEventBased(viewportContent);
        this.domInitialized = true;

        // Create background elements
        this.createBackgroundElements(viewportContent);

        // Generate container bounds
        this.generateContainerBounds();

        // Emit containers ready event
        this.emitContainersReady();
    }

    /**
     * Schedule event-based initialization retry
     */
    private scheduleEventBasedInitialization(): void {
        if (!this.domInitialized) {
            const domReadyHandler = () => {
                const viewportContent = d3.select(`.viewport-content-${this.chartId}`);
                if (!viewportContent.empty()) {
                    this.createStandardContainersEventBased(viewportContent);
                    this.createBackgroundElements(viewportContent);
                    this.generateContainerBounds();
                    this.emitContainersReady();
                    this.domInitialized = true;
                }
            };

            setTimeout(() => {
                if (!this.domInitialized) {
                    domReadyHandler();
                }
            }, 100);
        }
    }

    /**
     * Create standard containers - event-driven approach
     */
    private createStandardContainersEventBased(chartContent: any): void {
        if (this.containersReadyEmitted) {
            return;
        }

        this.createStandardContainers(chartContent);
        this.containersReadyEmitted = true;
    }

    /**
     * Create standard containers with chart ID indexes
     */
    private createStandardContainers(chartContent: any): void {
        // Z-order: background → muhurta-sectors → lines-overlay
        const containers = [
            { class: 'background-group', id: `background-container-${this.chartId}` },
            { class: 'muhurta-sectors-group', id: `muhurta-sectors-container-${this.chartId}` },
            { class: 'lines-overlay', id: `lines-overlay-${this.chartId}` }
        ];

        // Force correct z-order by removing and recreating all layers
        containers.forEach(container => {
            (chartContent as any).select(`.${container.class}`).remove();
        });

        // Create layers in correct z-order
        containers.forEach(container => {
            const group = (chartContent as any).append('g')
                .attr('class', container.class)
                .attr('id', container.id);

            // Create muhurta placeholder for specialized renderer
            if (container.class === 'muhurta-sectors-group') {
                group.append('g')
                    .attr('class', 'muhurta-divisions-layer')
                    .attr('id', `muhurta-divisions-${this.chartId}`)
                    .style('display', 'none');

                devLog(`🎯 TabChartCoordinator[${this.chartId}]: Created muhurta placeholder with ID muhurta-divisions-${this.chartId}`);
            }

            devLog(`🎯 TabChartCoordinator[${this.chartId}]: Created layer ${container.class} with proper z-order`);
        });
    }

    /**
     * Create background elements (12 houses) with chart ID index
     */
    private createBackgroundElements(chartContent: any): void {
        const backgroundGroup = (chartContent as any).select('.background-group');

        if (backgroundGroup.empty()) {
            devWarn(`⚠️ TabChartCoordinator[${this.chartId}]: Background group not found`);
            return;
        }

        // Create houses only once
        const existingHouses = backgroundGroup.selectAll('.house-background').size();
        if (existingHouses === 12) {
            devLog(`🏠 TabChartCoordinator[${this.chartId}]: Background houses already exist, skipping creation`);
            return;
        }

        devLog(`🏠 TabChartCoordinator[${this.chartId}]: Creating ${12 - existingHouses} missing background houses`);

        // Create 12 background rectangles for houses
        for (let house = 0; house < 12; house++) {
            const position = this.calculateHousePosition(house);

            backgroundGroup.append('rect')
                .attr('class', `house-background house-${house} chart-${this.chartId}`)
                .attr('data-house', house)
                .attr('data-chart-id', this.chartId)
                .attr('x', position.x)
                .attr('y', position.y)
                .attr('width', position.width)
                .attr('height', position.height)
                .attr('fill', chartColorPresetManager.getCurrentPreset().detailsPanel.background)
                .attr('stroke', chartColorPresetManager.getCurrentPreset().detailsPanel.borderDefault)
                .attr('stroke-width', 1)
                .attr('opacity', 1);
        }
    }

    /**
     * Calculate house position
     */
    private calculateHousePosition(house: number): { x: number, y: number, width: number, height: number } {
        const viewportSize = 700;
        const chartSize = 690;
        const cellSize = chartSize / 4;
        const offsetX = (viewportSize - chartSize) / 2;
        const offsetY = (viewportSize - chartSize) / 2;

        const positions = [
            { x: 0, y: 0 },                    // 0: 12-й дом
            { x: cellSize, y: 0 },             // 1: 1-й дом (Lagna)
            { x: cellSize * 2, y: 0 },         // 2: 2-й дом
            { x: cellSize * 3, y: 0 },         // 3: 3-й дом
            { x: cellSize * 3, y: cellSize },  // 4: 4-й дом
            { x: cellSize * 3, y: cellSize * 2 }, // 5: 5-й дом
            { x: cellSize * 3, y: cellSize * 3 }, // 6: 6-й дом
            { x: cellSize * 2, y: cellSize * 3 }, // 7: 7-й дом
            { x: cellSize, y: cellSize * 3 },  // 8: 8-й дом
            { x: 0, y: cellSize * 3 },         // 9: 9-й дом
            { x: 0, y: cellSize * 2 },         // 10: 10-й дом
            { x: 0, y: cellSize }              // 11: 11-й дом
        ];

        const pos = positions[house] || { x: 0, y: 0 };

        return {
            x: pos.x + offsetX,
            y: pos.y + offsetY,
            width: cellSize,
            height: cellSize
        };
    }

    /**
     * Generate container bounds
     */
    private generateContainerBounds(): void {
        const containerBounds = new Map();

        const mainBounds = { width: 700, height: 700, x: 0, y: 0 };

        containerBounds.set(`background-container-${this.chartId}`, mainBounds);

        // Bounds for 12 houses
        for (let house = 0; house < 12; house++) {
            const houseBounds = this.calculateHousePosition(house);
            containerBounds.set(`${house}-${this.chartId}`, houseBounds);
        }

        this.updateContainers(containerBounds);

        if (this.currentOptions.debugMode) {
            devLog(`📦 TabChartCoordinator[${this.chartId}]: Generated bounds for ${containerBounds.size} containers`);
        }
    }

    /**
     * Emit containers ready event with chart ID
     */
    private emitContainersReady(): void {
        if (this.containersReadyEmitted) {
            return;
        }

        const containerBounds: { [key: string]: any } = {};
        this.containers.forEach((bounds, id) => {
            containerBounds[id] = bounds;
        });

        this.eventStream.emit({
            id: `containers-ready-${this.chartId}-${Date.now()}`,
            type: 'containers.ready',
            source: `tab-chart-coordinator-${this.chartId}`,
            chartId: this.chartId,
            timestamp: Date.now(),
            metadata: {
                containerBounds,
                containerIds: Array.from(this.containers.keys()),
                chartId: this.chartId
            }
        });

        this.containersReadyEmitted = true;
        devLog(`📨 TabChartCoordinator[${this.chartId}]: Emitted containers.ready event`);
    }

    /**
     * Check if this chart is currently visible/active
     */
    private isChartVisible(): boolean {
        try {
            const multipleChartsContext = window.multipleChartsContext;
            if (multipleChartsContext && multipleChartsContext.activeChartId) {
                const isActive = multipleChartsContext.activeChartId === this.chartId;
                return isActive;
            }

            // Fallback: check DOM visibility
            const tabElement = document.querySelector(`[data-chart-id="${this.chartId}"]`);
            const isVisible = tabElement && !tabElement.classList.contains('hidden') &&
                             getComputedStyle(tabElement).display !== 'none';
            return !!isVisible;

        } catch (error) {
            devWarn(`⚠️ TabChartCoordinator[${this.chartId}]: Visibility check failed, assuming visible:`, error);
            return true;
        }
    }

    /**
     * Set tab active status and coordinate chakra initialization
     */
    public setTabActive(isActive: boolean): void {
        const wasActive = this.coordinatorStatus.tab.isActive;
        this.coordinatorStatus.tab.isActive = isActive;

        devLog(`🎯 TabChartCoordinator[${this.chartId}]: Tab activation changed: ${wasActive} → ${isActive}`);

        if (isActive && !wasActive && this.currentChartData) {
            // Tab became active - re-coordinate if we have chart data
            this.coordinateChart(this.currentChartData, this.currentOptions);
        }
    }

    /**
     * Setup event subscriptions with chart ID filtering
     */
    private setupEventSubscriptions(): void {
        // DOM ready events
        this.eventSubscriptions.push(
            this.eventStream.subscribe(`dom.ready.${this.chartId}`, (event: any) => {
                this.initializeDOMStructure();
            })
        );

        // Container ready events
        this.eventSubscriptions.push(
            this.eventStream.subscribe('containers.ready', (event: any) => {
                if (event.chartId === this.chartId || !event.chartId) {
                    if (event.metadata?.containerBounds) {
                        const boundsMap = new Map<string, ContainerBounds>();

                        for (const [id, bounds] of Object.entries(event.metadata.containerBounds)) {
                            boundsMap.set(id, bounds as ContainerBounds);
                        }

                        this.updateContainers(boundsMap);
                    }
                }
            })
        );

        // Zoom changes for display mode
        this.eventSubscriptions.push(
            this.eventStream.subscribe(`ZOOM_CHANGED_${this.chartId}`, async (event: any) => {
                const mode = this.getDisplayModeFromZoom(event.zoomLevel || 1.0);
                await this.switchDisplayMode(mode);
            })
        );

        // Listen for completion events from specialized renderers
        this.eventSubscriptions.push(
            this.eventStream.subscribe('graha.render.completed', (event: any) => {
                if (event.chartId === this.chartId) {
                    this.coordinatorStatus.chakras.grahaCompleted = true;
                    devLog(`✅ TabChartCoordinator[${this.chartId}]: Graha render completed`);
                }
            })
        );

        this.eventSubscriptions.push(
            this.eventStream.subscribe('muhurta.render.completed', (event: any) => {
                if (event.chartId === this.chartId) {
                    this.coordinatorStatus.chakras.muhurtaCompleted = true;
                    devLog(`✅ TabChartCoordinator[${this.chartId}]: Muhurta render completed`);
                }
            })
        );
    }

    /**
     * Determine display mode from zoom level
     */
    private getDisplayModeFromZoom(zoomLevel: number): 'symbols' | 'short' {
        return zoomLevel <= 1.0 ? 'symbols' : 'short';
    }

    /**
     * Cleanup resources
     */
    cleanup(): void {
        // Unsubscribe from all event subscriptions
        this.eventSubscriptions.forEach(sub => sub.unsubscribe());
        this.eventSubscriptions = [];

        this.containers.clear();
        this.isInitialized = false;
        devLog(`🧹 TabChartCoordinator[${this.chartId}]: Cleanup completed`);
    }

    /**
     * Recreate background elements with new colors
     */
    recreateBackgroundElements(): void {
        const chartContent = d3.select(`.viewport-content-${this.chartId}`);
        if (!chartContent.empty()) {
            this.createBackgroundElements(chartContent);
        }
    }

    /**
     * Get coordinator statistics
     */
    getStats() {
        return {
            chartId: this.chartId,
            isInitialized: this.isInitialized,
            containerCount: this.containers.size,
            currentMode: this.currentOptions.displayMode,
            coordinatorStatus: { ...this.coordinatorStatus },
            chakraSettings: {
                showGrahaChakra: this.currentOptions.showGrahaChakra,
                showMuhurtaChakra: this.currentOptions.showMuhurtaChakra
            }
        };
    }
}